import {
  BooleanDimension,
  CalendarDateDimension,
  FinancialRateDimension,
  frAupaSelectedDimension,
  StringsEnumDimension,
  StringsWithDataEnumDimension,
} from '@orus.eu/dimensions'
import {
  type FrAupaAssistanceFormula,
  type FrAupaDeductibleLevel,
  type FrAupaFormula,
  frAupaProductVersions,
} from '@orus.eu/product'
import type { ParameterlessTranslationKey } from '@orus.eu/translations'

export const frAupaProductVersionDimension = new StringsEnumDimension({
  name: 'frAupaProductVersion',
  displayKeys: { name: 'dimension_name_fr_aupa_product_version' },
  values: frAupaProductVersions,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frAupaRatingDateDimension = new CalendarDateDimension({
  name: 'frAupaRatingDate',
  displayKeys: { name: 'dimension_name_fr_aupa_rating_date' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

const frAupaFormulaEntries = [
  ['third-party', { labelKey: 'dimension_label_fr_aupa_formula_third_party' }],
  ['third-party-plus', { labelKey: 'dimension_label_fr_aupa_formula_third_party_plus' }],
  ['all-risks', { labelKey: 'dimension_label_fr_aupa_formula_all_risks' }],
] as const satisfies [FrAupaFormula, { labelKey: ParameterlessTranslationKey }][]

export const frAupaFormulaDimension = new StringsWithDataEnumDimension({
  name: 'frAupaFormula',
  displayKeys: { name: 'dimension_name_fr_aupa_formula' },
  entries: frAupaFormulaEntries,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: true,
  inputLocking: { lockedInQuoteFor: { client: frAupaSelectedDimension } },
} as const)

export const frAupaRecommendedFormulaDimension = new StringsWithDataEnumDimension({
  name: 'frAupaRecommendedFormula',
  displayKeys: { name: 'dimension_name_fr_aupa_sales_recommended_formula' },
  entries: [...frAupaFormulaEntries, ['none', { labelKey: 'dimension_label_fr_aupa_recommended_formula_none' }]],
  // TODO INS-2198: lock it in endorsement and renewal, and show the advice only in quote, so the client never sees it change
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frAupaAssistanceFormulaDimension = new StringsWithDataEnumDimension({
  name: 'frAupaAssistanceFormula',
  displayKeys: { name: 'dimension_name_fr_aupa_assistance_formula' },
  entries: [
    ['silver', { labelKey: 'dimension_label_fr_aupa_assistance_formula_silver' }],
    ['gold', { labelKey: 'dimension_label_fr_aupa_assistance_formula_gold' }],
    ['gold-plus', { labelKey: 'dimension_label_fr_aupa_assistance_formula_gold_plus' }],
  ] as const satisfies [FrAupaAssistanceFormula, { labelKey: ParameterlessTranslationKey }][],
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: { lockedInQuoteFor: { client: frAupaSelectedDimension } },
} as const)

export const frAupaDeductibleLevelDimension = new StringsWithDataEnumDimension({
  name: 'frAupaDeductibleLevel',
  displayKeys: { name: 'dimension_name_fr_aupa_deductible_level' },
  entries: [
    ['standard', { labelKey: 'dimension_label_fr_aupa_deductible_level_standard' }],
    ['reduced', { labelKey: 'dimension_label_fr_aupa_deductible_level_reduced' }],
  ] as const satisfies [FrAupaDeductibleLevel, { labelKey: ParameterlessTranslationKey }][],
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: { lockedInQuoteFor: { client: frAupaSelectedDimension } },
} as const)

export const frAupaFinancialLossCoverAlreadyHeldDimension = new BooleanDimension({
  name: 'frAupaFinancialLossCoverAlreadyHeld',
  displayKeys: {
    name: 'dimension_name_fr_aupa_financial_loss_cover_already_held',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: { lockedInQuoteFor: { client: frAupaSelectedDimension } },
} as const)

export const frAupaFinancialLossCoverAlreadyHeldRequiredDimension = new BooleanDimension({
  name: 'frAupaFinancialLossCoverAlreadyHeldRequired',
  displayKeys: { name: 'dimension_name_fr_aupa_financial_loss_cover_already_held_required' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frAupaHasClaimsInLastThreeYearsDimension = new BooleanDimension({
  name: 'frAupaHasClaimsInLastThreeYears',
  displayKeys: {
    name: 'dimension_name_fr_aupa_has_claims_in_last_three_years',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: { lockedInQuoteFor: { client: frAupaSelectedDimension } },
} as const)

export const frAupaOverchargeRateDimension = new FinancialRateDimension({
  name: 'frAupaOverchargeRate',
  displayKeys: { name: 'dimension_name_fr_aupa_overcharge_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: true,
  inputLocking: undefined,
  configuration: { min: 0, max: 10, step: 0.01 },
})
